import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
} from 'react-native';
import Svg, { Path, Rect, Line } from 'react-native-svg';
import { AdminColors } from '../../../constants/adminColors';
import FontFamily from '../../../../../constants/fonts';
import ColumnPickerModal, { ALL_COLUMNS } from './ColumnPickerModal';

const ColumnsIcon = () => (
  <Svg width="15" height="15" viewBox="0 0 24 24" fill="none">
    <Path
      d="M12 3H3a1 1 0 0 0-1 1v16a1 1 0 0 0 1 1h9M12 3h9a1 1 0 0 1 1 1v16a1 1 0 0 1-1 1h-9M12 3v18"
      stroke={AdminColors.blue800}
      strokeWidth="2"
      strokeLinecap="round"
    />
  </Svg>
);

const ExportIcon = () => (
  <Svg width="15" height="15" viewBox="0 0 24 24" fill="none">
    <Path
      d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3"
      stroke={AdminColors.blue800}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </Svg>
);

interface LocationTableToolbarProps {
  visibleColumns: string[];
  onColumnsChange: (columns: string[]) => void;
  onExport?: () => void;
  title?: string;
  description?: string;
  totalItems?: number;
}

const LocationTableToolbar: React.FC<LocationTableToolbarProps> = ({
  visibleColumns,
  onColumnsChange,
  onExport,
  title = 'Location Records',
  description,
  totalItems,
}) => {
  const [showColumnPicker, setShowColumnPicker] = useState(false);

  const visibleCount = visibleColumns.length;
  const totalCount = ALL_COLUMNS.length;

  return (
    <>
      <View style={styles.toolbar}>
        {/* Title / description */}
        <View style={styles.titleWrap}>
          <Text style={styles.title}>{title}</Text>
          {description ? (
            <Text style={styles.description}>{description}</Text>
          ) : totalItems !== undefined ? (
            <Text style={styles.description}>{totalItems} record{totalItems !== 1 ? 's' : ''}</Text>
          ) : null}
        </View>

        <View style={{ flex: 1 }} />

        {/* Column picker button */}
        <TouchableOpacity
          style={styles.toolBtn}
          onPress={() => setShowColumnPicker(true)}
          activeOpacity={0.7}
        >
          <ColumnsIcon />
          <Text style={styles.toolBtnTxt}>Columns</Text>
          <View style={styles.countBadge}>
            <Text style={styles.countBadgeTxt}>{visibleCount}</Text>
          </View>
        </TouchableOpacity>

        {/* Export button */}
        {onExport && (
          <TouchableOpacity style={styles.toolBtn} onPress={onExport} activeOpacity={0.7}>
            <ExportIcon />
            <Text style={styles.toolBtnTxt}>Export</Text>
          </TouchableOpacity>
        )}
      </View>

      {/* Column picker modal */}
      <ColumnPickerModal
        visible={showColumnPicker}
        visibleColumns={visibleColumns}
        onClose={() => setShowColumnPicker(false)}
        onApply={onColumnsChange}
      />
    </>
  );
};

const styles = StyleSheet.create({
  toolbar: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    gap: 12,
    flexWrap: 'wrap',
    paddingHorizontal: 15,
    paddingVertical: 13,
    borderBottomWidth: 1,
    borderBottomColor: AdminColors.borderDefault,
  },
  titleWrap: {
    gap: 3,
    marginRight: 4,
  },
  title: {
    fontSize: 14,
    fontWeight: '700',
    color: AdminColors.textPrimary,
    fontFamily: FontFamily.bold,
  },
  description: {
    fontSize: 11,
    color: AdminColors.textMuted,
    fontFamily: FontFamily.regular,
  },
  toolBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    height: 34,
    paddingHorizontal: 12,
    borderWidth: 1,
    borderColor: AdminColors.blue800,
    borderRadius: 18,
    backgroundColor: AdminColors.bgSurface,
  },
  toolBtnTxt: {
    fontSize: 11,
    fontWeight: '700',
    color: AdminColors.blue800,
    fontFamily: FontFamily.bold,
  },
  countBadge: {
    height: 18,
    minWidth: 18,
    paddingHorizontal: 4,
    borderRadius: 9,
    backgroundColor: AdminColors.blue50,
    alignItems: 'center',
    justifyContent: 'center',
  },
  countBadgeTxt: {
    fontSize: 10,
    fontWeight: '700',
    color: AdminColors.blue800,
    fontFamily: FontFamily.bold,
  },
});

export default LocationTableToolbar;
